// Archivo para crear el formulario de las actividades
// src/components/forms/ActivityForm.jsx
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import api from '../../services/api'
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material'
import { CloudUpload, Delete, ExpandMore } from '@mui/icons-material'

/**
 * ActivityForm: Formulario para registrar actividad y gestionar documentos.
 * - Carga dinámica de códigos (acción, contacto, resultado).
 * - Subida de archivos con botón dedicado.
 * - Sección de documentos en Accordion.
 */
const ActivityForm = ({ initialData = {}, onChange, productId }) => {
  const [formData, setFormData] = useState({
    actionCode: '',
    contactCode: '',
    resultCode: '',
    comments: '',
    documents: [],
    ...initialData,
  })

  const [codes, setCodes] = useState({
    actionCodes: [],
    contactCodes: [],
    resultCodes: [],
  })
  const [loadingCodes, setLoadingCodes] = useState(true)

  useEffect(() => {
    const fetchCodes = async () => {
      const token = localStorage.getItem('authToken')
      const requestConfig = {
        headers: { Authorization: `Bearer ${token}` },
        ...(productId ? { params: { productId } } : {}),
      }

      setCodes({
        actionCodes: [],
        contactCodes: [],
        resultCodes: [],
      })
      setLoadingCodes(true)

      try {
        const [aRes, cRes, rRes] = await Promise.all([
          api.get('/codes/actions', requestConfig),
          api.get('/codes/contacts', requestConfig),
          api.get('/codes/results', requestConfig),
        ])
        setCodes({
          actionCodes: aRes.data,
          contactCodes: cRes.data,
          resultCodes: rRes.data,
        })
      } catch (err) {
        console.error('Error fetching codes:', err)
      } finally {
        setLoadingCodes(false)
      }
    }
    fetchCodes()
  }, [productId])

  useEffect(() => {
    if (onChange) onChange(formData)
  }, [formData, onChange])

  const handleFieldChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files)
    setFormData((prev) => {
      const updated = { ...prev, documents: [...prev.documents, ...files] }
      if (onChange) onChange(updated)
      return updated
    })
  }

  const handleRemoveFile = (index) => {
    setFormData((prev) => {
      const docs = prev.documents.filter((_, i) => i !== index)
      const updated = { ...prev, documents: docs }
      if (onChange) onChange(updated)
      return updated
    })
  }

  if (loadingCodes) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Campos de actividad */}
      <TextField
        select
        label="Acción"
        name="actionCode"
        value={formData.actionCode}
        onChange={handleFieldChange}
        fullWidth
        required
      >
        {codes.actionCodes.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Contacto"
        name="contactCode"
        value={formData.contactCode}
        onChange={handleFieldChange}
        fullWidth
        required
      >
        {codes.contactCodes.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Resultado"
        name="resultCode"
        value={formData.resultCode}
        onChange={handleFieldChange}
        fullWidth
        required
      >
        {codes.resultCodes.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Comentarios (opcional)"
        name="comments"
        value={formData.comments}
        onChange={handleFieldChange}
        fullWidth
        multiline
        rows={2}
      />

      {/* Botón de subida de documentos */}
      <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
        Cargar Documentos
        <input
          type="file"
          hidden
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.gif,.mp3,.wav"
          onChange={handleFileUpload}
        />
      </Button>

      {/* Sección Accordion para visualizar documentos */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>
            Documentos Adjuntos ({formData.documents.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {formData.documents.length > 0 ? (
            <List>
              {formData.documents.map((file, idx) => (
                <ListItem key={idx} disableGutters>
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(2)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFile(idx)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No hay documentos cargados.</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

ActivityForm.propTypes = {
  initialData: PropTypes.shape({
    actionCode: PropTypes.string,
    contactCode: PropTypes.string,
    resultCode: PropTypes.string,
    comments: PropTypes.string,
    documents: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  }),
  onChange: PropTypes.func.isRequired,
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default ActivityForm
